"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { DiscoveredProject } from "../../core/discovery";
import { Select } from "./Select";
import { NEW, NONE, composeScope, projectsIn, seedPicker, subspacesOf } from "./seed";
import { formSlot, type FormTheme } from "./styles";

export type ProjectPickerProps = {
  projects: DiscoveredProject[];
  /** Current proposal, `space/project` or `space/subspace/project`, used to preselect. */
  initial: string;
  /** Called with the proposal (or "" while incomplete). */
  onChange: (scope: string) => void;
  disabled?: boolean;
  theme: FormTheme;
  placeholders?: { space?: string; subspace?: string; project?: string };
};

/**
 * «Spazio», «Sottospazio» and «Progetto» as offered by Flow, each with a «Nuovo…» entry
 * that turns into a free field. A project can sit directly in the space («Nessun
 * sottospazio») or in one of its subspaces. The proposal is `space/project` or
 * `space/subspace/project`.
 */
export function ProjectPicker({
  projects,
  initial,
  onChange,
  disabled,
  theme,
  placeholders,
}: ProjectPickerProps) {
  const spaces = useMemo(
    () => [...new Set(projects.map((p) => p.space))].sort((a, b) => a.localeCompare(b)),
    [projects],
  );
  const [space, setSpace] = useState("");
  const [spaceText, setSpaceText] = useState("");
  const [subspace, setSubspace] = useState("");
  const [subspaceText, setSubspaceText] = useState("");
  const [project, setProject] = useState("");
  const [projectText, setProjectText] = useState("");
  // Until the fields are seeded they report nothing: reporting "" on the first render
  // overwrote the saved project, and a connected app showed empty fields.
  const [seeded, setSeeded] = useState(false);
  // Once a person has touched the fields, a late proposal must not overwrite them.
  const touched = useRef(false);
  const touch =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      touched.current = true;
      set(v);
    };

  // Preselect from the proposal — which may arrive after the list — until someone types.
  useEffect(() => {
    if (touched.current) return;
    const seed = seedPicker(initial, projects);
    setSpace(seed.space);
    setSpaceText(seed.spaceText);
    setSubspace(seed.subspace);
    setSubspaceText(seed.subspaceText);
    setProject(seed.project);
    setProjectText(seed.projectText);
    setSeeded(true);
  }, [initial, projects]);

  const spaceValue = space === NEW ? spaceText.trim() : space;
  const subspaces = space && space !== NEW ? subspacesOf(projects, space) : [];
  const subspaceValue = subspace === NEW ? subspaceText.trim() : subspace === NONE ? "" : subspace;
  const subspaceChosen = subspace !== "" && (subspace !== NEW || subspaceValue !== "");
  const offered =
    space && space !== NEW && subspace && subspace !== NEW
      ? projectsIn(projects, space, subspace)
      : [];
  // A new space or subspace has no projects yet: straight to the free field.
  const projectIsNew = project === NEW || offered.length === 0;
  const projectValue = projectIsNew ? projectText.trim() : project;

  useEffect(() => {
    if (!seeded) return;
    onChange(subspaceChosen ? composeScope(spaceValue, subspaceValue, projectValue) : "");
  }, [seeded, spaceValue, subspaceChosen, subspaceValue, projectValue, onChange]);

  const label = (text: string) => <span {...formSlot(theme, "label")}>{text}</span>;
  const hint = (text: string) => (
    <div {...formSlot(theme, "hint", { display: "block", paddingTop: 14 })}>{text}</div>
  );
  const free = (value: string, set: (v: string) => void, placeholder: string, name: string) => (
    <input
      {...formSlot(theme, "input", { marginTop: 8 })}
      aria-label={name}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      spellCheck={false}
      onChange={(e) => set(e.target.value)}
    />
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
      <div {...formSlot(theme, "field")}>
        {label("Spazio")}
        <Select
          theme={theme}
          ariaLabel="Spazio"
          disabled={disabled}
          value={space}
          placeholder="Scegli uno spazio"
          onChange={(v) => {
            touched.current = true;
            setSpace(v);
            // A space without subspaces needs no choice: its projects sit in it directly.
            setSubspace(v === NEW || subspacesOf(projects, v).length === 0 ? NONE : "");
            setProject("");
          }}
          options={[
            ...spaces.map((s) => ({
              value: s,
              label: s,
              hint: String(projects.filter((p) => p.space === s).length),
            })),
            { value: NEW, label: "Nuovo spazio…", always: true },
          ]}
        />
        {space === NEW
          ? free(
              spaceText,
              touch(setSpaceText),
              placeholders?.space ?? "nome-spazio",
              "Nuovo spazio",
            )
          : null}
      </div>
      <div {...formSlot(theme, "field")}>
        {label("Sottospazio")}
        {space ? (
          <Select
            theme={theme}
            ariaLabel="Sottospazio"
            disabled={disabled}
            value={subspace}
            placeholder="Scegli un sottospazio"
            onChange={(v) => {
              touched.current = true;
              setSubspace(v);
              setProject("");
            }}
            options={[
              {
                value: NONE,
                label: "Nessun sottospazio",
                hint: space !== NEW ? String(projectsIn(projects, space, NONE).length) : undefined,
                always: true,
              },
              ...subspaces.map((s) => ({
                value: s,
                label: s,
                hint: String(projectsIn(projects, space, s).length),
              })),
              { value: NEW, label: "Nuovo sottospazio…", always: true },
            ]}
          />
        ) : (
          hint("Scegli prima lo spazio.")
        )}
        {subspace === NEW
          ? free(
              subspaceText,
              touch(setSubspaceText),
              placeholders?.subspace ?? "nome-sottospazio",
              "Nuovo sottospazio",
            )
          : null}
      </div>
      <div {...formSlot(theme, "field")}>
        {label("Progetto")}
        {space && subspace && offered.length > 0 ? (
          <Select
            theme={theme}
            ariaLabel="Progetto"
            disabled={disabled}
            value={project}
            placeholder="Scegli un progetto"
            onChange={touch(setProject)}
            options={[
              ...offered.map((p) => ({
                value: p.project,
                label: p.title,
                hint: p.title !== p.project ? p.project : undefined,
              })),
              { value: NEW, label: "Nuovo progetto…", always: true },
            ]}
          />
        ) : null}
        {space && subspace && projectIsNew
          ? free(
              projectText,
              touch(setProjectText),
              placeholders?.project ?? "nome-progetto",
              "Nuovo progetto",
            )
          : null}
        {!space
          ? hint("Scegli prima lo spazio.")
          : !subspace
            ? hint("Scegli il sottospazio.")
            : null}
      </div>
    </div>
  );
}
